import {
  PDF_FONT_SIZE,
  type ReportTableCell,
  type ReportTableProps,
} from '@/components';
import {
  type ReportOrganizingTableFormat3Model,
  type ReportOrganizingTableFormat3ProjectModel,
  format3ColumnType,
  type projectDetailUrlTypeEnum,
} from '@/features/mold/models';
import { getProjectDetailPageUrl } from '@/features/project/_utils/project';
import { ActionOfReflectionStatusLabelModifier } from '@/features/project/projects/_models/project';
import { t } from '@/i18n';
import { formatAmountForPdf } from '@/utils';
import type { CSSProperties } from 'react';
import type { z } from 'zod';
import { centerTextStyle } from '../../pdf/pdf-utils';
import { format3ValueConverter, getformat3HeaderModifier } from './get-csv';

const MONEY_STYLE: CSSProperties = { textAlign: 'right' };

const headerStyle: CSSProperties = {
  fontSize: PDF_FONT_SIZE.MD,
  textAlign: 'center',
};

const DEFAULT_CELL_RATIO = 80 as const;
const DEFAULT_CELL_RATIO_WIDTH = `${DEFAULT_CELL_RATIO}px` as const;

const reflectionStatusCellRatios = [
  DEFAULT_CELL_RATIO_WIDTH,
  DEFAULT_CELL_RATIO_WIDTH,
  DEFAULT_CELL_RATIO_WIDTH,
  'auto',
];

const getFormat3PdfHeader = ({
  year,
}: {
  year: number;
}): ReportTableProps['header'] => {
  const modifier = getformat3HeaderModifier({ year, isPdf: true });
  return [
    {
      value: modifier.projectNumber,
      ratio: DEFAULT_CELL_RATIO_WIDTH,
      textStyle: headerStyle,
    },
    {
      value: modifier.name,
      ratio: DEFAULT_CELL_RATIO_WIDTH,
      textStyle: headerStyle,
    },
    {
      type: 'multiple',
      ratio: `${DEFAULT_CELL_RATIO * 2}px`,
      cell: {
        vertical: true,
        flexCells: [
          {
            value: t('fiscal-year-with-value', { year: year - 1 }),
            textStyle: headerStyle,
          },
          {
            type: 'multiple',
            cell: {
              flexCells: [
                {
                  value: modifier.previousFinalizedAmountTotal,
                  textStyle: headerStyle,
                },
                {
                  value: modifier.previousExecutionAmountTotal,
                  textStyle: headerStyle,
                },
              ],
            },
          },
        ],
      },
    },
    {
      type: 'multiple',
      ratio: '350px',
      cell: {
        vertical: true,
        flexCells: [
          {
            value: t('review-type-public-process', { year: year - 1 }),
            textStyle: headerStyle,
          },
          {
            value: modifier.resultOfPublishing,
            textStyle: headerStyle,
          },
        ],
      },
    },
    {
      value: modifier.finalizedAmount,
      ratio: DEFAULT_CELL_RATIO_WIDTH,
      textStyle: headerStyle,
    },
    {
      value: modifier.requestedAmount,
      ratio: DEFAULT_CELL_RATIO_WIDTH,
      textStyle: headerStyle,
    },
    {
      value: modifier.differenceBetweenRequestedAmountAndInitialBudget,
      ratio: DEFAULT_CELL_RATIO_WIDTH,
      textStyle: headerStyle,
    },
    {
      type: 'multiple',
      ratio: 'auto',
      cell: {
        vertical: true,
        flexCells: [
          {
            value: t('reflection-status-based-on-opinion'),
            textStyle: headerStyle,
          },
          {
            type: 'multiple',
            cell: {
              cellRatios: reflectionStatusCellRatios,
              flexCells: [
                {
                  value: modifier.accountingTypeName,
                  textStyle: headerStyle,
                },
                {
                  value: modifier.reflectedAmount,
                  textStyle: headerStyle,
                },
                {
                  value: modifier.reflectionStatusAction,
                  textStyle: headerStyle,
                },
                {
                  value: modifier.reflectionStatusContext,
                  textStyle: headerStyle,
                },
              ],
            },
          },
        ],
      },
    },
  ];
};

type TableBodyElement = ReportTableProps['data'][number];

const getFormat3PdfBody = ({
  project,
  isDebugMode,
  projectDetailUrlType,
}: {
  project: Array<z.infer<typeof ReportOrganizingTableFormat3ProjectModel>>;
  isDebugMode: boolean;
  projectDetailUrlType?: z.infer<typeof projectDetailUrlTypeEnum> | null;
}): ReportTableProps['data'] => {
  return project.flatMap<TableBodyElement>((data) => {
    return {
      row: format3ColumnType.options.flatMap<ReportTableCell>((key) => {
        switch (key) {
          case 'previousExecutionAmountTotal':
          case 'reflectedAmount':
          case 'reflectionStatusAction':
          case 'reflectionStatusContext':
          case 'projectDetailPageUrl': {
            return [];
          }
          case 'previousFinalizedAmountTotal': {
            return {
              type: 'multiple',
              cell: {
                flexCells: [
                  {
                    ...centerTextStyle,
                    value: format3ValueConverter.previousFinalizedAmountTotal({
                      data,
                      type: 'pdf',
                      isDebugMode,
                    }),
                    textStyle: MONEY_STYLE,
                  },
                  {
                    ...centerTextStyle,
                    value: format3ValueConverter.previousExecutionAmountTotal({
                      data,
                      type: 'pdf',
                      isDebugMode,
                    }),
                    textStyle: MONEY_STYLE,
                  },
                ],
              },
            };
          }
          case 'accountingTypeName': {
            return {
              type: 'multiple',
              cell: {
                cellRatios: reflectionStatusCellRatios,
                flexCells: [
                  {
                    ...centerTextStyle,
                    value: format3ValueConverter.accountingTypeName(
                      data.accountingTypeType,
                      data.accountingTypeName,
                      data.accountTitleName,
                    ),
                  },
                  {
                    ...centerTextStyle,
                    value: format3ValueConverter.reflectedAmount({
                      data,
                      type: 'pdf',
                      isDebugMode,
                    }),
                    textStyle: MONEY_STYLE,
                  },
                  {
                    ...centerTextStyle,
                    value: data.reflectionStatusAction
                      ? ActionOfReflectionStatusLabelModifier[
                          data.reflectionStatusAction
                        ]
                      : '',
                  },
                  {
                    ...centerTextStyle,
                    value: data.reflectionStatusContext ?? '',
                  },
                ],
              },
            };
          }
          case 'finalizedAmount': {
            return {
              value: format3ValueConverter.finalizedAmount({
                data,
                type: 'pdf',
                isDebugMode,
              }),
              textStyle: MONEY_STYLE,
            };
          }
          case 'requestedAmount': {
            return {
              value: format3ValueConverter.requestedAmount({
                data,
                type: 'pdf',
                isDebugMode,
              }),
              textStyle: MONEY_STYLE,
            };
          }
          case 'differenceBetweenRequestedAmountAndInitialBudget': {
            return {
              value:
                format3ValueConverter.differenceBetweenRequestedAmountAndInitialBudget(
                  {
                    data,
                    type: 'pdf',
                    isDebugMode,
                  },
                ),
              textStyle: MONEY_STYLE,
            };
          }
          case 'name': {
            return {
              type: 'link',
              href: getProjectDetailPageUrl({
                isPS: projectDetailUrlType === 'public',
                projectId: data.id,
              }),
              value: data.name,
            };
          }
          case 'projectNumber':
          case 'resultOfPublishing': {
            return { value: data[key] ?? '' };
          }
        }
      }),
    };
  });
};

const getFormat3PdfFooter = ({
  summary,
}: {
  summary: z.infer<typeof ReportOrganizingTableFormat3Model>['summary'];
}): ReportTableProps['footer'] => {
  return [
    {
      row: format3ColumnType.options.flatMap<ReportTableCell>((key) => {
        switch (key) {
          case 'projectNumber':
          case 'previousExecutionAmountTotal':
          case 'reflectedAmount':
          case 'reflectionStatusAction':
          case 'reflectionStatusContext':
          case 'projectDetailPageUrl': {
            return [];
          }
          case 'resultOfPublishing': {
            return { value: '' };
          }
          case 'name': {
            return {
              value: t('total'),
              colSpan: 2,
              textStyle: { textAlign: 'center' },
            };
          }
          case 'previousFinalizedAmountTotal': {
            return {
              type: 'multiple',
              cell: {
                flexCells: [
                  {
                    value: formatAmountForPdf(
                      summary.previousFinalizedAmountTotal,
                    ),
                    textStyle: MONEY_STYLE,
                  },
                  {
                    value: formatAmountForPdf(
                      summary.previousExecutionAmountTotal,
                    ),
                    textStyle: MONEY_STYLE,
                  },
                ],
              },
            };
          }
          case 'accountingTypeName': {
            return {
              type: 'multiple',
              cell: {
                cellRatios: reflectionStatusCellRatios,
                flexCells: [
                  { value: '' },
                  {
                    value: formatAmountForPdf(summary.reflectedAmount),
                    textStyle: MONEY_STYLE,
                  },
                  { value: '' },
                  { value: '' },
                ],
              },
            };
          }
          default: {
            return {
              value: formatAmountForPdf(summary[key]),
              textStyle: MONEY_STYLE,
            };
          }
        }
      }),
    },
  ];
};

export const getFormat3Pdf = ({
  year,
  data,
  isDebugMode,
  projectDetailUrlType,
}: {
  year: number;
  data: z.infer<typeof ReportOrganizingTableFormat3Model>;
  isDebugMode: boolean;
  projectDetailUrlType?: z.infer<typeof projectDetailUrlTypeEnum> | null;
}): {
  header: ReportTableProps['header'];
  body: ReportTableProps['data'];
  footer: ReportTableProps['footer'];
} => {
  return {
    header: getFormat3PdfHeader({ year }),
    body: getFormat3PdfBody({
      project: data.projects,
      isDebugMode,
      projectDetailUrlType,
    }),
    footer: getFormat3PdfFooter({ summary: data.summary }),
  };
};
